'use client';

import { ReactNode } from 'react';

import {
  TAssessmentImageType,
  TImageMetaPosition,
  TImageMetaPositionAlt,
} from '@northladder/apis';
import {
  AssessmentBackScreenAnimation,
  AssessmentBlackScreenAnimation,
  AssessmentWhiteScreenAnimation,
  AssessmentBlackToWhiteAnimation,
} from '@northladder/design-system';
import { TTextLocalized } from '@northladder/utilities';

import { translations } from '../../translations';

import { TAssessmentScreen } from '../types';

export interface ITargetAssessmentConfig {
  screen: TAssessmentScreen;
  thumbnail: string;
  intro: {
    title: TTextLocalized;
    imageUrl: string;
    ctaText: TTextLocalized;
    subCTAPreText?: TTextLocalized;
    subCTALabelText?: TTextLocalized;
  };
  instructions: {
    title: TTextLocalized;
    HeroAnimation: ({ className }: { className: string }) => ReactNode;
  };
  previewAndUpload: {
    description: TTextLocalized;
    captureSuccess: {
      title: TTextLocalized;
    };
    onSuccessGoTo: TAssessmentScreen;
    ctaText: TTextLocalized;
  };
  nextScreenInstructions?: {
    screen: TAssessmentScreen;
    title: TTextLocalized;
    ConfirmAnimation: ({ className }: { className: string }) => ReactNode;
    ctaText: TTextLocalized;
    subCTAPreText?: TTextLocalized;
    altCtaText?: TTextLocalized;
  };
}

/**
 * This hook gets a desired device image assessment config for the provided id.
 *
 * @param imageId The assessment image id from the backend.
 * @returns ITargetAssessmentConfig The image config object.
 */
export const useTargetAssessmentConfig = (
  imageId: TAssessmentImageType | TImageMetaPositionAlt | TImageMetaPosition
) => {
  let config: ITargetAssessmentConfig;

  const {
    deviceBack,
    frontWhite,
    frontBlack,
    frontOfDeviceInfo,
    frontBlackOfDeviceInfo,
    frontWhiteOfDeviceInfo,
    backOfDeviceInfo,
    leftOfDeviceInfo,
    rightOfDeviceInfo,
    topOfDeviceInfo,
    bottomOfDeviceInfo,
  } = translations.assessmentConfig;

  switch (imageId) {
    case 'Front_White_Cropped': {
      config = {
        screen: 'assess-white-screen',
        thumbnail: '/images/assessment-images/assess-white-screen.png',
        intro: {
          title: frontWhite.intro.title,
          imageUrl: '/images/assessment-images/assess-white-screen.png',
          ctaText: frontWhite.intro.ctaText,
          subCTAPreText: frontWhite.intro.subCTAPreText,
          subCTALabelText: frontWhite.intro.subCTALabelText,
        },
        instructions: {
          title: frontWhite.instructions.title,
          HeroAnimation: AssessmentWhiteScreenAnimation,
        },
        previewAndUpload: {
          description: frontWhite.previewAndUpload.description,
          captureSuccess: {
            title: frontWhite.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: 'assess-back-screen',
          ctaText: frontWhite.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'Back_NA_Cropped': {
      config = {
        screen: 'assess-back-screen',
        thumbnail: '/images/assessment-images/assess-back-screen.png',
        intro: {
          title: deviceBack.intro.title,
          imageUrl: '/images/assessment-images/assess-back-screen.png',
          ctaText: deviceBack.intro.ctaText,
          subCTALabelText: deviceBack.intro.subCTALabelText,
        },
        instructions: {
          title: deviceBack.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: deviceBack.previewAndUpload.description,
          captureSuccess: {
            title: deviceBack.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: 'assessment-done',
          ctaText: deviceBack.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'FRONT': {
      config = {
        screen: 'assess-front',
        thumbnail: '/images/assessment-images/FRONT.png',
        intro: {
          title: frontOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/FRONT.png',
          ctaText: frontOfDeviceInfo.intro.ctaText,
          subCTALabelText: frontOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: frontOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: frontOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: frontOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: frontOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'FRONT_BLACK': {
      config = {
        screen: 'assess-front-black',
        thumbnail: '/images/assessment-images/FRONT_BLACK.png',
        intro: {
          title: frontBlackOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/FRONT_BLACK.png',
          ctaText: frontBlackOfDeviceInfo.intro.ctaText,
          subCTALabelText: frontBlackOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: frontBlackOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: frontBlackOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: frontBlackOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: frontBlackOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'FRONT_WHITE': {
      config = {
        screen: 'assess-front-white',
        thumbnail: '/images/assessment-images/FRONT_WHITE.png',
        intro: {
          title: frontWhiteOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/FRONT_WHITE.png',
          ctaText: frontWhiteOfDeviceInfo.intro.ctaText,
          subCTALabelText: frontWhiteOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: frontWhiteOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: frontWhiteOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: frontWhiteOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: frontWhiteOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'BACK': {
      config = {
        screen: 'assess-back',
        thumbnail: '/images/assessment-images/BACK.png',
        intro: {
          title: backOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/BACK.png',
          ctaText: backOfDeviceInfo.intro.ctaText,
          subCTALabelText: backOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: backOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: backOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: backOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: backOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'LEFT': {
      config = {
        screen: 'assess-left',
        thumbnail: '/images/assessment-images/LEFT.png',
        intro: {
          title: leftOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/LEFT.png',
          ctaText: leftOfDeviceInfo.intro.ctaText,
          subCTALabelText: leftOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: leftOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: leftOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: leftOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: leftOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'RIGHT': {
      config = {
        screen: 'assess-right',
        thumbnail: '/images/assessment-images/RIGHT.png',
        intro: {
          title: rightOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/RIGHT.png',
          ctaText: rightOfDeviceInfo.intro.ctaText,
          subCTALabelText: rightOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: rightOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: rightOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: rightOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: rightOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'TOP': {
      config = {
        screen: 'assess-top',
        thumbnail: '/images/assessment-images/TOP.png',
        intro: {
          title: topOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/TOP.png',
          ctaText: topOfDeviceInfo.intro.ctaText,
          subCTALabelText: topOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: topOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: topOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: topOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: topOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'BOTTOM': {
      config = {
        screen: 'assess-bottom',
        thumbnail: '/images/assessment-images/BOTTOM.png',
        intro: {
          title: bottomOfDeviceInfo.intro.title,
          imageUrl: '/images/assessment-images/BOTTOM.png',
          ctaText: bottomOfDeviceInfo.intro.ctaText,
          subCTALabelText: bottomOfDeviceInfo.intro.subCTALabelText,
        },
        instructions: {
          title: bottomOfDeviceInfo.instructions.title,
          HeroAnimation: AssessmentBackScreenAnimation,
        },
        previewAndUpload: {
          description: bottomOfDeviceInfo.previewAndUpload.description,
          captureSuccess: {
            title: bottomOfDeviceInfo.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: '/',
          ctaText: bottomOfDeviceInfo.previewAndUpload.ctaText,
        },
      };
      break;
    }
    case 'Front_Black_Cropped':
    default: {
      config = {
        screen: 'assess-black-screen',
        thumbnail: '/images/assessment-images/assess-black-screen.png',
        intro: {
          title: frontBlack.intro.title,
          imageUrl: '/images/assessment-images/assess-black-screen.png',
          ctaText: frontBlack.intro.ctaText,
          subCTAPreText: frontBlack.intro.subCTAPreText,
          subCTALabelText: frontBlack.intro.subCTALabelText,
        },
        instructions: {
          title: frontBlack.instructions.title,
          HeroAnimation: AssessmentBlackScreenAnimation,
        },
        previewAndUpload: {
          description: frontBlack.previewAndUpload.description,
          captureSuccess: {
            title: frontBlack.previewAndUpload.captureSuccessTitle,
          },
          onSuccessGoTo: 'assess-white-screen',
          ctaText: frontBlack.previewAndUpload.ctaText,
        },
        nextScreenInstructions: {
          screen: 'assess-white-screen',
          title: frontBlack.nextScreenInstructions.title,
          ConfirmAnimation: AssessmentBlackToWhiteAnimation,
          ctaText: frontBlack.nextScreenInstructions.ctaText,
          altCtaText: frontBlack.nextScreenInstructions.altCtaText,
        },
      };
    }
  }

  return config;
};
